<template>
  <div class="svoi-pagination">
    <SvoiPaginationLink
      v-if="showArrows"
      class="pagination-arrow"
      :link="prevPage"
      :class="{ '-disabled': !prevPage }"
      :component-type="componentType"
    >
      <SvoiIcon icon="arrow/arrow-left-2" size="s" />
    </SvoiPaginationLink>
    <div
      v-for="(page, index) in pages"
      :key="`page_${index}`"
      class="page-wrapper"
      :class="{ '-active': page.number === prepareActivePage }"
    >
      <SvoiPaginationLink
        :link="page.link && page.number !== prepareActivePage ? page.link : undefined"
        class="page"
        :component-type="componentType"
      >
        {{ page.title }}
      </SvoiPaginationLink>
    </div>
    <SvoiPaginationLink
      v-if="showArrows"
      class="pagination-arrow"
      :link="nextPage"
      :class="{ '-disabled': !nextPage }"
      :component-type="componentType"
    >
      <SvoiIcon icon="arrow/arrow-right-3" size="s" />
    </SvoiPaginationLink>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watchEffect } from 'vue-demi'
import { SvoiIcon } from '@svoi-ui/components/icon'
import type { PageInfo } from '@svoi-ui/interfaces/pagination'
import { LinkTypes, LinkTypesEnum } from '@svoi-ui/interfaces/link-type'
import { isString } from '@svoi-ui/shared/utils/guards'
import SvoiPaginationLink from './SvoiPaginationLink.vue'
import { BasePaginationComponent, useBasePagination } from './BasePagination'

export default defineComponent({
  name: 'SvoiPagination',
  components: { SvoiIcon, SvoiPaginationLink },
  props: {
    ...BasePaginationComponent.props,
    baseUrl: {
      type: String,
      required: true,
      default: '/'
    },
    componentType: {
      type: String as PropType<LinkTypes | string>,
      default: LinkTypesEnum.nuxtLink,
      validator: (value: unknown) => {
        return isString(value) && [LinkTypesEnum.a.toString(), LinkTypesEnum.nuxtLink.toString()].includes(value)
      }
    }
  },
  setup(props) {
    const { prepareActivePage, getPages, getLink } = useBasePagination(props)

    const pages = ref<Array<PageInfo>>([])
    const prevPage = ref<string | undefined>(undefined)
    const nextPage = ref<string | undefined>(undefined)

    watchEffect(() => {
      const baseUrl = props.baseUrl
      pages.value = getPages(baseUrl)
      prevPage.value = prepareActivePage.value > 1 ? getLink(baseUrl, prepareActivePage.value - 1) : undefined
      nextPage.value =
        prepareActivePage.value < props.totalPages ? getLink(baseUrl, prepareActivePage.value + 1) : undefined
    })

    return { prepareActivePage, pages, prevPage, nextPage }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/pagination.scss';
</style>
